/*
 * Platform Analytics Experience — Audience Data Synthesis view model.
 *
 * This module restores the canonical Audience Intelligence layer to the
 * redesigned platform analytics pages. It is a pure mapping layer over the
 * governed resolution returned by GET /api/audience-intelligence/:advertiserId
 * (services/audienceIntelligenceService.js on the backend): it renders only
 * fields that canonical payload actually carries and never invents audience
 * facts, confidence, or freshness that the record does not declare.
 *
 * Isolation contract: nothing here is imported by ConnectedPlatformAnalyticsPage,
 * ProgrammaticChannelAnalyticsPage, or any excluded page (Facebook, Instagram,
 * Cinema), and this module does not modify the shared resolution hook they use.
 */

/*
 * Platform support classification for the audience-synthesis contract.
 *
 * The backend audience registry (SUPPORTED_PLATFORMS in
 * services/audienceIntelligenceService.js) governs which platform keys can
 * carry a platform-specific synthesis. Platforms outside that registry are
 * canonically mapped to the governed shared scope ("overall") — the backend's
 * own documented manual-fallback scope — and every render is labeled as
 * shared. No platform-specific synthesis is implied where the contract
 * cannot store one.
 */
import { buildAudienceIntelligenceReport } from "./audienceIntelligenceReport";

export const AUDIENCE_SYNTHESIS_SUPPORT = {
  PLATFORM_SPECIFIC: "platform_specific",
  SHARED_ONLY: "shared_only",
};

export const SHARED_SYNTHESIS_SCOPE_KEY = "overall";

export const SHARED_SYNTHESIS_LABEL =
  "Shared audience synthesis — no platform-specific interpretation is available for this source.";

/*
 * A synthesis whose record has not been updated in this many days is labeled
 * aged. This mirrors the 45-day staleness bound already governing reporting
 * fallbacks (MAX_LATEST_FALLBACK_AGE_DAYS / the Cross-Platform freshness
 * penalty floor); it labels honestly — it never suppresses the record.
 */
export const AUDIENCE_SYNTHESIS_AGED_DAYS = 45;

const SHARED_SCOPE_KEYS = new Set(["overall", "cross_platform", "omni"]);

const CONFIDENCE_LABELS = {
  live: "Live",
  high: "High confidence",
  medium: "Medium confidence",
  directional: "Directional",
  planning: "Planning",
};

function normalizeText(value) {
  return String(value ?? "").trim();
}

function toStringList(value, limit = 6) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => normalizeText(entry))
    .filter(Boolean)
    .slice(0, limit);
}

export function resolveAudienceRequestPlatform(profile) {
  if (!profile) return null;
  const support = profile.audienceSupport || AUDIENCE_SYNTHESIS_SUPPORT.SHARED_ONLY;
  if (support === AUDIENCE_SYNTHESIS_SUPPORT.PLATFORM_SPECIFIC) {
    return profile.audiencePlatformKey || profile.platformKey || null;
  }
  return SHARED_SYNTHESIS_SCOPE_KEY;
}

function daysBetween(isoStamp, nowMs) {
  const parsed = Date.parse(isoStamp || "");
  if (!Number.isFinite(parsed)) return null;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  return Math.max(0, (now - parsed) / 86400000);
}

function formatDateLabel(isoStamp) {
  const parsed = Date.parse(isoStamp || "");
  if (!Number.isFinite(parsed)) return null;
  return new Date(parsed).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/*
 * Maps the canonical resolution payload into everything the Audience Data
 * Synthesis panel renders.
 *
 * requestState: "idle" | "loading" | "error" | "resolved"
 * role: "superadmin" | "client" — provenance internals (source type/name,
 *   resolution tier mechanics) render for superadmin only; the client variant
 *   is restricted to the approved client-facing fields the backend already
 *   emits for client viewers.
 */
export function buildAudienceSynthesisViewModel({
  resolution = null,
  requestState = "resolved",
  support = AUDIENCE_SYNTHESIS_SUPPORT.PLATFORM_SPECIFIC,
  role = "superadmin",
  nowMs = null,
} = {}) {
  if (requestState === "idle") {
    return { state: "idle" };
  }
  if (requestState === "loading") {
    return { state: "loading" };
  }
  if (requestState === "error") {
    return {
      state: "error",
      message:
        "The audience synthesis could not be resolved right now. Existing records are unchanged — retry by reloading or changing scope.",
    };
  }

  const tier = normalizeText(resolution?.tier) || "unavailable";
  const card = resolution?.card || null;

  if (!resolution || tier === "unavailable" || !card) {
    return {
      state: "unavailable",
      message:
        "No governed audience analysis has been published for this advertiser and platform scope. Nothing is inferred in its place.",
    };
  }

  const resolvedPlatform = normalizeText(resolution.platform).toLowerCase();
  const shared =
    support === AUDIENCE_SYNTHESIS_SUPPORT.SHARED_ONLY || SHARED_SCOPE_KEYS.has(resolvedPlatform);

  if (tier === "planner_context") {
    return {
      state: "planner",
      statusLabel: normalizeText(card.status) || "Planning Audience Context",
      summary: normalizeText(card.summary),
      bullets: toStringList(card.bullets, 6),
      shared,
      sharedLabel: shared ? SHARED_SYNTHESIS_LABEL : null,
      note: normalizeText(card.note) || null,
      disclaimer: normalizeText(card.disclaimer) || null,
    };
  }

  const updatedAt = normalizeText(resolution.updatedAt) || null;
  const ageDays = daysBetween(updatedAt, nowMs);
  const aged = ageDays != null && ageDays > AUDIENCE_SYNTHESIS_AGED_DAYS;
  const confidenceKey = normalizeText(resolution.confidence || card.confidence).toLowerCase();

  /*
   * The presentation report. The governed payload carries the same analysis
   * through several properties at once (composed card summary, narrative body,
   * opening summary, client-facing prompt), so the panel renders THIS rather
   * than each property in turn — one canonical body, structured, with every
   * sentence emitted exactly once.
   */
  const report = buildAudienceIntelligenceReport({
    summary: card.summary,
    narrative: resolution.primaryAudienceNarrative,
    audienceSummary: resolution.audienceSummary,
    clientFacingPrompt: resolution.clientFacingPrompt,
    recommendedAction: resolution.recommendedAction,
    strongestSegments: resolution.strongestSegments,
    emergingSegments: resolution.emergingSegments,
    ageSignals: resolution.ageSignals,
    genderSignals: resolution.genderSignals,
    deviceSignals: resolution.deviceSignals,
    geographySignals: resolution.geographySignals,
  });

  const viewModel = {
    state: "synthesis",
    tier,
    statusLabel: normalizeText(card.status) || (tier === "live" ? "Live Feed" : "Manual Intelligence"),
    report,
    summary: normalizeText(card.summary),
    narrative: normalizeText(resolution.primaryAudienceNarrative) || null,
    bullets: toStringList(card.bullets, 8),
    strongestSegments: toStringList(resolution.strongestSegments, 4),
    emergingSegments: toStringList(resolution.emergingSegments, 3),
    recommendedAction: normalizeText(resolution.recommendedAction) || null,
    confidenceKey: confidenceKey || null,
    confidenceLabel: CONFIDENCE_LABELS[confidenceKey] || null,
    dateRangeLabel: normalizeText(resolution.dateLabel || card.dateRangeLabel) || null,
    updatedAt,
    updatedAtLabel: formatDateLabel(updatedAt),
    aged,
    agedLabel:
      aged && updatedAt
        ? `Aged synthesis — last updated ${formatDateLabel(updatedAt)}. Confirm it still reflects the current flight before acting on it.`
        : null,
    shared,
    sharedLabel: shared ? SHARED_SYNTHESIS_LABEL : null,
    campaignId: resolution.campaignId ?? null,
    resolvedPlatform: resolvedPlatform || null,
    note: normalizeText(card.note) || null,
    disclaimer: normalizeText(card.disclaimer) || null,
    provenance: null,
  };

  if (role === "superadmin") {
    viewModel.provenance = {
      source: normalizeText(resolution.source) || "none",
      isFallback: Boolean(resolution.isFallback),
      sourceType: normalizeText(card.sourceType) || null,
      sourceName: normalizeText(card.sourceName) || null,
      resolvedPlatform: resolvedPlatform || null,
    };
  }

  return viewModel;
}

/*
 * Deterministic one-sentence answers for the EDEN command bar. Derived from
 * the already-resolved view model — no model call, no fetch.
 */
export function describeAudienceSynthesisForEden(viewModel, { heroLabel = "this platform" } = {}) {
  if (!viewModel || viewModel.state === "idle") {
    return `Choose an advertiser first — the audience synthesis resolves against a specific advertiser scope.`;
  }
  if (viewModel.state === "loading") {
    return "The audience synthesis is still resolving.";
  }
  if (viewModel.state === "error") {
    return "The audience synthesis could not be resolved right now.";
  }
  if (viewModel.state === "unavailable") {
    return `No governed audience analysis is published for ${heroLabel} in this scope.`;
  }
  if (viewModel.state === "planner") {
    return `No published audience synthesis exists yet — the panel shows approved planning targeting as context, clearly labeled.`;
  }
  const sharedNote = viewModel.shared ? " It is a shared synthesis, not a platform-specific interpretation." : "";
  /* The executive read, not the composed body — the composed body restates the
   * same opening on legacy records. */
  const lead =
    viewModel.report?.executiveRead?.headline ||
    viewModel.summary ||
    "An audience synthesis is available.";
  return `${lead}${sharedNote}`;
}

export function describeAudienceSynthesisTimestamp(viewModel) {
  if (!viewModel || viewModel.state !== "synthesis") {
    return "No published audience synthesis is resolved for this scope, so there is no generation timestamp to report.";
  }
  if (!viewModel.updatedAtLabel) {
    return "The resolved audience synthesis does not declare an update timestamp.";
  }
  return `The resolved audience synthesis was last updated ${viewModel.updatedAtLabel}${
    viewModel.dateRangeLabel ? ` and covers ${viewModel.dateRangeLabel}` : ""
  }.${viewModel.aged ? " It is older than the 45-day freshness bound — treat it as aged." : ""}`;
}
